import React, {useCallback, useEffect, useState} from 'react';
import {DropdownSection, DropdownV2} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import cx from 'classnames';
import {useKeyboardNavigation} from '../../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {setIsReactPopupOpen} from '../../../../_shared/GWTBridge/invoke/netForm';
import {sendMonitor} from '../../../../utils/monitoring';
import {useEventListener} from '../../../../hooks/useEventListener';
import {iSearchDropdownCloseKey} from '../../../../_legacy/FieldSearch/utils/keys';
import {AutomationsDropdownItem} from './parts/Item';
import {helpPopoverRef} from '../../../../_shared/Help/constants';
import {ButtonTrigger} from './parts/Trigger/Trigger';
import {AutomationSettingsItem, macroItem} from './constants';
import {Form} from '../../../../stores/Form/Form';
import {automationDropdownID} from '../../testIDs';
import {MacroItem} from './parts/MacroItem';
import styles from './styles.module.scss';
import {texts} from '../../texts';
import {getMacroNameById, getMacroIdByKey, startMacro} from '../../../Macro/actions';
import {getIsHtmlForm} from '../../utils';

type Props = {
    onSelect: (item: AutomationSettingsItem & {form: Form}) => void;
    settingsItems: Array<AutomationSettingsItem>;
    macroSubmenu?: boolean;
    disabled?: boolean;
    componentClasses?: string;
    contentComponentClasses?: string;
    form: Form;
    useBridgeAPI?: boolean;
};

export const AutomationDropdown = observer(
    ({
        settingsItems = [],
        macroSubmenu = true,
        componentClasses,
        contentComponentClasses,
        onSelect,
        form,
        disabled,
        useBridgeAPI
    }: Props) => {
        const [open, setOpen] = useState(false);
        const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
        const isHtmlForm = getIsHtmlForm();

        const listLength = isHtmlForm ? 1 : settingsItems.length + 1;

        const [preventClose, setPreventClose] = useState(false);

        const handleToggleSubMenuDropdown = useCallback(() => {
            setIsSubmenuOpen(prevState => !prevState);
        }, []);

        const handleSelectFocusedIndex = async () => {
            if (focusedIndex === 0 && macroSubmenu) {
                setIsSubmenuOpen(true);
            } else {
                await onItemClick(settingsItems[focusedIndex - 1]);
            }
        };

        const handleNavigateRight = () => {
            if (macroSubmenu && focusedIndex === 0) {
                setIsSubmenuOpen(prevState => !prevState);
            }
        };

        const {focusedIndex, handleKeyboardNavigation, setFocusedIndex} = useKeyboardNavigation({
            initialFocusedIndex: 0,
            listLength,
            pageSize: 5,
            onSelect: handleSelectFocusedIndex,
            onNavigateRight: handleNavigateRight
        });

        useEffect(() => {
            if (open) {
                setFocusedIndex(0);
            }
        }, [open]);

        const handleClose = useCallback(() => {
            if (!preventClose) {
                setOpen(false);
                setIsSubmenuOpen(false);
            }
            setPreventClose(false);
            setIsReactPopupOpen(false);
        }, [preventClose]);

        const handleActivateMacro = useCallback(
            macroID => {
                const macroName = getMacroNameById(macroID);
                sendMonitor(`PlayMacro"${macroName}"`);
                handleClose();
                startMacro(macroID);
            },
            [handleClose]
        );

        const handleKeyDown = useCallback(
            e => {
                //not use "e.key" for macOS support
                const num = e.code?.replace('Digit', '').replace('Numpad', '');
                if (e.altKey && Number(num) <= 9) {
                    const macroID = getMacroIdByKey(num);
                    if (macroID) {
                        handleActivateMacro(macroID);
                    }
                }
            },
            [handleActivateMacro]
        );

        useEventListener('keydown', handleKeyDown, window, true);

        const handleShowHelp = (e, text: string) => {
            e.stopPropagation();

            helpPopoverRef.current?.showHelpPopover({
                title: texts.help_popover_title,
                content: text,
                anchor: e.target,
                onClose: () => {
                    setPreventClose(false);
                }
            });
            setPreventClose(true);
        };

        const handleOpen = () => {
            setOpen(true);
            setIsReactPopupOpen(true);
        };

        const toggleOpen = () => {
            open ? handleClose() : handleOpen();
        };

        const onItemClick = async item => {
            onSelect({...item, form});
            sendMonitor(`setting item click - ${item.title}`);
            handleClose();
        };

        const closeMacroSubmemu = useCallback(() => {
            setIsSubmenuOpen(false);
        }, []);

        const handleDropdownNavigation = useCallback(
            e => {
                if (!isSubmenuOpen && !preventClose) {
                    handleKeyboardNavigation(e);
                }
            },
            [isSubmenuOpen, preventClose, handleKeyboardNavigation]
        );

        useEffect(() => {
            if (focusedIndex === listLength) {
                setFocusedIndex(0);
            }
        }, [focusedIndex, listLength]);

        const currentFocusedIndex = !isSubmenuOpen ? focusedIndex : null;

        return (
            <DropdownV2
                oldStyles
                dataTestId={automationDropdownID}
                componentClasses={cx(styles.component, componentClasses, {
                    [styles.disabled]: disabled
                })}
                isOpen={open}
                width="contentWidth"
                contentComponentClasses={contentComponentClasses}
                onClose={handleClose}
                trigger={
                    <ButtonTrigger
                        isOpen={open}
                        onClick={toggleOpen}
                        disabled={disabled}
                        buttonComponentClasses={styles.triggerButton}
                    />
                }
                isCloseKeyCallback={iSearchDropdownCloseKey}
                onKeyDown={handleDropdownNavigation}
                escClosable={!preventClose}
            >
                <DropdownSection>
                    {macroSubmenu && (
                        <MacroItem
                            key={macroItem.title}
                            isOpenSubmenu={isSubmenuOpen}
                            openMacro={onItemClick}
                            handleOpenSubMenu={handleToggleSubMenuDropdown}
                            index={0}
                            currentFocusedIndex={currentFocusedIndex}
                            handleShowHelp={handleShowHelp}
                            handleClose={handleClose}
                            closeSubMenu={closeMacroSubmemu}
                            useBridgeAPI={useBridgeAPI}
                        />
                    )}
                    {!isHtmlForm &&
                        settingsItems.map((item, index) => (
                            <AutomationsDropdownItem
                                item={item}
                                key={item.title}
                                onItemClick={onItemClick}
                                index={macroSubmenu ? index + 1 : index}
                                currentFocusedIndex={currentFocusedIndex}
                                handleShowHelp={handleShowHelp}
                            />
                        ))}
                </DropdownSection>
            </DropdownV2>
        );
    }
);
